/**
 * Central registry for every ministrymemos-* localStorage key the app uses.
 *
 * Why this exists:
 * - Avoid silent typos (e.g. `ministrymemos-church-admin-ruth-hidden-v1`
 *   was historically misnamed for *all* personas, not just Ruth).
 * - Give us a single place to clear keys on sign-out so a different user
 *   on the same browser does not inherit private state.
 * - Make it easy to grep for "what state lives in localStorage" and to
 *   bump versions when shapes change.
 *
 * Versioning: bump the trailing `-vN` on a key when its stored shape
 * changes incompatibly. Old keys remain in `LEGACY_KEYS` so we can
 * proactively remove them on next load.
 */

export const LOCAL_STORAGE_KEYS = {
  // The Memo Board
  memoStack: "ministrymemos-memo-stack-v1",
  memoExecutionPlans: "ministrymemos-memo-execution-plans-v2",
  memoDecisionMemory: "ministrymemos-memo-decision-memory-v1",
  memoSnooze: "ministrymemos-memo-snooze-v1",
  memoLastTriage: "ministrymemos-memo-last-triage-v1",
  /** Memo → desk overrides (v2 of the pre-unification persona overrides). */
  memoDeskOverrides: "ministrymemos-memo-desk-overrides-v1",
  /** Legacy (pre-unification) memo → persona overrides; read once for migration. */
  memoPersonaOverrides: "ministrymemos-memo-persona-overrides-v1",

  // Personal assistant dock (chat dock at the bottom of The Memo Board)
  paDockExpanded: "ministrymemos-pa-dock-expanded-v1",
  paDockHidden: "ministrymemos-pa-dock-hidden-v1",

  // Sticky-memo-level agent picker
  stickyMemoPadAgent: "ministrymemos-sticky-memo-pad-agent-v1",

  // Smart Memo Pad shell
  smartMemoPads: "ministrymemos-smart-memo-pads-v2",

  /**
   * The AI thread that was open last (Office / Smart Memo Pad share one).
   * Messages themselves live in `ai_conversation_messages`; this is only the
   * pointer, so a remount or refresh resumes the conversation instead of
   * dropping the user into a blank one. Cleared on sign-out with every other key.
   */
  aiActiveConversation: "ministrymemos-ai-active-conversation-v1",

  /**
   * Half-typed AI messages, per surface ("office", "pad", `pad:<memoId>`), so a
   * remount or refresh doesn't throw away what someone was in the middle of
   * writing. Cleared as soon as the message is sent.
   */
  aiComposerDraft: (surface: string) => `ministrymemos-ai-composer-${surface}-v1`,

  /** The page open in the Mission builder with unsaved edits (page + edit origin). */
  missionBuilderDraft: "ministrymemos-mission-builder-draft-v1",

  /** The Mission builder's unsent "build with AI" prompt. */
  missionBuilderPrompt: "ministrymemos-mission-builder-prompt-v1",

  // Recurring jobs (legacy local cache; primary store is Supabase)
  recurringJobTemplatesLegacy: "ministrymemos-recurring-job-templates-v1",

  /**
   * Per-scope list/table view preferences (sort, filters, visible columns, page size).
   * Example: `ministrymemos-data-view-basic-directory-v1`
   */
  dataViewPrefs: (scope: string) => `ministrymemos-data-view-${scope}-v1`,

  /** Board/list/compact view mode for a feature scope. */
  viewMode: (scope: string) => `ministrymemos-view-mode-${scope}-v1`,

  /** Saved Notion-style member table views (per user + church). */
  membersNotionViews: (churchId: string, userId: string) =>
    `ministrymemos-members-notion-views-${churchId}-${userId}-v1`,

  /** Configurable home group names for Notion kanban (per church). */
  membersNotionHomeGroups: (churchId: string) =>
    `ministrymemos-members-notion-home-groups-${churchId}-v1`,

  /** Configurable Small group field options (per church). */
  membersSmallGroupOptions: (churchId: string) =>
    `ministrymemos-members-small-group-options-${churchId}-v1`,

  /** Module workspace draft pages (per user + church + module). @deprecated Migrated to myPages */
  moduleWorkspace: (moduleKey: string, churchId: string, userId: string) =>
    `ministrymemos-module-workspace-${moduleKey}-${churchId}-${userId}-v1`,

  /** Central My Pages draft store (per user + church). @deprecated Migrated to user_pages table */
  myPages: (churchId: string, userId: string) =>
    `ministrymemos-my-pages-${churchId}-${userId}-v2`,

  /** Active user page selection (per user + church). */
  userPagesActive: (churchId: string, userId: string) =>
    `ministrymemos-user-pages-active-${churchId}-${userId}-v1`,

  /** Marks People workspace drafts migrated into My Pages. */
  myPagesPeopleMigrated: (churchId: string, userId: string) =>
    `ministrymemos-my-pages-people-migrated-${churchId}-${userId}-v1`,

  /** Resizable rota schedule column widths (per rota). */
  rotaScheduleColumnWidths: (rotaId: string) =>
    `ministrymemos-rota-schedule-columns-${rotaId}-v1`,

  /** Module scratch database table layout (per database). */
  moduleDatabaseTableLayout: (databaseId: string) =>
    `ministrymemos-module-database-layout-${databaseId}-v1`,

  /** Rota schedule table layout prefs (per rota). */
  rotaScheduleTableLayout: (rotaId: string) =>
    `ministrymemos-rota-schedule-layout-${rotaId}-v1`,

  /** People setup page view preferences (per user + church). */
  peopleSetupView: (churchId: string, userId: string) =>
    `ministrymemos-people-setup-view-${churchId}-${userId}-v1`,

  /** Recent AI page-builder drafts (per church). */
  aiPageBuilds: (churchId: string) => `ministrymemos-pages-ai-builds-${churchId}-v1`,

  /** Expanded sidebar nav groups. */
  sidebarExpandedGroups: "ministrymemos-sidebar-expanded-groups-v1",

  /** Office panel dismissed on The Memo Board. */
  officePanelHidden: "ministrymemos-office-panel-hidden-v1",

  /** Per-module colour scheme overrides. */
  moduleColorSchemes: "ministrymemos-module-color-schemes-v1",

  /**
   * Last user id seen signed in on this browser. Drives the sign-in sweep in
   * AuthContext — see `clearAllMinistryMemosKeys`. Deliberately swept along
   * with everything else: once the state is gone there is nothing to compare
   * against, so an absent marker is the same as a clean browser.
   */
  lastSignedInUser: "ministrymemos-last-signed-in-user-v1",
} as const;

/**
 * Keys that intentionally survive sign-out, documented so "not swept" is
 * distinguishable from "nobody thought about it". Do not add these to the
 * registry — they are device-scoped, not user-scoped:
 *
 * - `mm_cookie_consent`, `session_timeout` — device/browser preferences.
 * - `pending_verification_email` — mid-flow, spans the signed-out state.
 * - `auth_security`, `join_form_security`, … (`useFormSecurity`) — brute-force
 *   lockout counters. Clearing these on sign-out would let an attacker reset a
 *   lockout by signing out.
 */

/**
 * Keys we no longer write but may still find on a returning user's machine.
 * On boot we proactively remove them.
 */
export const LEGACY_KEYS: readonly string[] = [
  "ministrymemos-memo-execution-plans-v1",
  "ministrymemos-smart-memo-pads-v1",
  "ministrymemos-church-admin-ruth-hidden-v1",
  // Underscored spelling, which slipped past the `ministrymemos-` sign-out
  // sweep. Superseded by `LOCAL_STORAGE_KEYS.moduleColorSchemes`.
  "ministrymemos_module_color_schemes",
];

/**
 * Legacy key *prefixes* we no longer write. Needed for stores that were keyed
 * per church/user before they joined this registry, where the full key is not
 * knowable up front and so cannot go in `LEGACY_KEYS`.
 */
export const LEGACY_KEY_PREFIXES: readonly string[] = [
  // AI page-builder drafts, before `LOCAL_STORAGE_KEYS.aiPageBuilds`. These
  // hold generated pages that can contain member data, and the old prefix
  // escaped the sign-out sweep entirely.
  "mm-pages-ai-builds:",
];

/** Every stored key starting with one of `prefixes`. */
function keysWithPrefix(prefixes: readonly string[]): string[] {
  const found: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && prefixes.some((p) => k.startsWith(p))) found.push(k);
  }
  return found;
}

/** Remove all stored ministrymemos-* keys (call on sign-out). */
export function clearAllMinistryMemosKeys(): void {
  if (typeof window === "undefined") return;
  try {
    // Legacy prefixes too: a user signing out here may still have pre-registry
    // copies on disk that the next user on this browser must not inherit.
    const toRemove = keysWithPrefix(["ministrymemos-", ...LEGACY_KEY_PREFIXES]);
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore quota / access errors
  }
}

/** Run once at app boot. Removes legacy keys we no longer support. */
export function pruneLegacyMinistryMemosKeys(): void {
  if (typeof window === "undefined") return;
  try {
    LEGACY_KEYS.forEach((k) => window.localStorage.removeItem(k));
    keysWithPrefix(LEGACY_KEY_PREFIXES).forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
